import React, { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useGlobal } from "@/context/GlobalContext";
import { StructureIcon } from "@/components/StructureIcon";
import { motion, AnimatePresence } from "framer-motion";
import { X, Search, ChevronRight, Building2, MapPin, ArrowUpRight, Loader2, Layers, Pin, PinOff } from "lucide-react";

const isBuilding = (label) => ["División", "Razón Social", "Estado"].includes(label);

export default function StructurePanel({ open, onClose, pinned = false, onTogglePin }) {
  const { structures, structureKey, setStructureKey, setNode, node } = useGlobal();
  const [query, setQuery] = useState("");
  const [openIds, setOpenIds] = useState({});

  const { data: nodes, isLoading } = useQuery({
    queryKey: ["ctx-tree", structureKey],
    queryFn: async () => (await api.get(`/context/tree`, { params: { structure: structureKey } })).data,
    enabled: (open || pinned) && !!structureKey,
  });

  const byParent = useMemo(() => {
    const list = nodes || [];
    const bp = {};
    list.forEach((n) => { (bp[n.parent_id] = bp[n.parent_id] || []).push(n); });
    return bp;
  }, [nodes]);

  const visibleRows = useMemo(() => {
    const list = nodes || [];
    const ids = new Set(list.map((n) => n.id));
    const roots = list.filter((n) => !n.parent_id || !ids.has(n.parent_id));
    const out = [];
    const walk = (arr, depth) => {
      arr.forEach((n) => {
        out.push({ node: n, depth });
        if (openIds[n.id] && byParent[n.id]) walk(byParent[n.id], depth + 1);
      });
    };
    walk(roots, 0);
    return out;
  }, [nodes, byParent, openIds]);

  const searchResults = useMemo(() => {
    if (!query.trim() || !nodes) return null;
    const q = query.toLowerCase();
    return nodes.filter((n) => n.name.toLowerCase().includes(q) || (n.razon_social || "").toLowerCase().includes(q)).slice(0, 40);
  }, [query, nodes]);

  const onToggle = (id) => setOpenIds((o) => ({ ...o, [id]: !o[id] }));
  const dismiss = () => { if (!pinned) onClose(); };
  const onSelect = (n) => {
    const path = [...(n.ancestors || []), { id: n.id, name: n.name, label: n.label }];
    setNode({ id: n.id, name: n.name, label: n.label, path });
    dismiss();
  };

  const body = (
    <>
            <div className="p-5 border-b border-slate-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <span className="text-[11px] font-bold uppercase tracking-wider px-2.5 py-1 rounded bg-red-600 text-white">Estructura</span>
                  <span className="font-display font-extrabold text-slate-900 text-lg">Grupo Tomza</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <button onClick={onTogglePin} data-testid="pin-structure-panel" title={pinned ? "Desfijar panel" : "Fijar panel para cambiar rápido entre plantas"}
                    className={`w-8 h-8 rounded-lg border hidden 2xl:flex items-center justify-center transition-colors ${pinned ? "bg-red-600 border-red-600 text-white" : "border-slate-300 text-slate-500 hover:bg-slate-100"}`}>
                    {pinned ? <PinOff className="w-4 h-4" /> : <Pin className="w-4 h-4" />}
                  </button>
                  {!pinned && (
                    <button onClick={onClose} className="w-8 h-8 rounded-lg border border-slate-300 flex items-center justify-center text-slate-500 hover:bg-slate-100" data-testid="close-structure-panel"><X className="w-4 h-4" /></button>
                  )}
                </div>
              </div>
              <p className="text-xs text-slate-500 mt-2">Elige la estructura de distribución y navega el árbol organizacional.</p>

              <div className="flex flex-wrap gap-1.5 mt-3">
                {structures.map((s) => (
                  <button
                    key={s.key}
                    onClick={() => { setStructureKey(s.key); setQuery(""); setOpenIds({}); }}
                    data-testid={`panel-structure-${s.key}`}
                    className={`flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-lg border transition-colors ${
                      s.key === structureKey ? "text-white border-transparent" : "bg-slate-100 text-slate-600 border-slate-200 hover:bg-slate-200"
                    }`}
                    style={s.key === structureKey ? { backgroundColor: s.color } : undefined}
                  >
                    <StructureIcon name={s.icon} className="w-3.5 h-3.5" />
                    {s.name}
                  </button>
                ))}
              </div>

              <div className="relative mt-3">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Buscar empresa, gerente o zona..."
                  data-testid="structure-search"
                  className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-slate-300 text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-red-500/40"
                />
              </div>
            </div>

            <div className="flex-1 overflow-auto p-3">
              <button
                onClick={() => { setNode(null); dismiss(); }}
                data-testid="select-entire-structure"
                className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-left mb-2 border transition-colors ${
                  !node ? "bg-red-50 border-red-200 text-red-700" : "border-slate-200 hover:bg-slate-100 text-slate-700"
                }`}
              >
                <Layers className="w-4 h-4 shrink-0" style={!node ? { color: "#DC2626" } : undefined} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold">Toda la estructura</p>
                  <p className="text-[11px] text-slate-400">Acumula toda tu estructura autorizada</p>
                </div>
                {!node && <ArrowUpRight className="w-4 h-4 text-red-500" />}
              </button>
              {isLoading ? (
                <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 text-red-600 animate-spin" /></div>
              ) : searchResults ? (
                searchResults.length ? searchResults.map((n) => (
                  <button key={n.id} onClick={() => onSelect(n)} data-testid={`search-result-${n.id}`} className="w-full flex items-center gap-2 px-3 py-2.5 rounded-lg hover:bg-slate-100 text-left">
                    {isBuilding(n.label) ? <Building2 className="w-4 h-4 text-red-600" /> : <MapPin className="w-4 h-4 text-red-600" />}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-slate-800 truncate">{n.name}</p>
                      <p className="text-[11px] text-slate-400 truncate">{n.label}{n.razon_social && n.razon_social !== n.name ? ` · ${n.razon_social}` : ""}</p>
                    </div>
                    <ArrowUpRight className="w-4 h-4 text-slate-300" />
                  </button>
                )) : <p className="text-center text-sm text-slate-400 py-10">Sin resultados</p>
              ) : (
                visibleRows.map(({ node, depth }) => {
                  const hasKids = !!byParent[node.id];
                  const Icon = isBuilding(node.label) ? Building2 : MapPin;
                  return (
                    <div key={node.id} className="flex items-center gap-1.5 pr-2 py-2 rounded-lg hover:bg-slate-100 group" style={{ paddingLeft: `${depth * 18 + 4}px` }} data-testid={`tree-row-${node.id}`}>
                      <button onClick={() => hasKids && onToggle(node.id)} className={`w-5 h-5 flex items-center justify-center ${hasKids ? "text-slate-400" : "opacity-0"}`}>
                        <ChevronRight className={`w-4 h-4 transition-transform ${openIds[node.id] ? "rotate-90" : ""}`} />
                      </button>
                      <Icon className="w-4 h-4 text-red-600 shrink-0" />
                      <button onClick={() => (hasKids ? onToggle(node.id) : onSelect(node))} className="flex-1 text-left min-w-0">
                        <span className={`text-sm truncate ${isBuilding(node.label) ? "font-bold text-slate-800" : "text-slate-700"}`}>{node.name}</span>
                      </button>
                      <span className="text-xs text-slate-400 tabular-nums">{node.sucursales}</span>
                      <button onClick={() => onSelect(node)} title="Seleccionar" data-testid={`tree-select-${node.id}`} className="text-slate-300 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity">
                        <ArrowUpRight className="w-4 h-4" />
                      </button>
                    </div>
                  );
                })
              )}
            </div>
    </>
  );

  if (pinned) {
    return (
      <aside data-testid="structure-panel-docked"
        className="hidden 2xl:flex w-[340px] shrink-0 bg-white border-r border-slate-200 sticky top-16 h-[calc(100vh-4rem)] flex-col overflow-hidden">
        {body}
      </aside>
    );
  }

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 z-50" onClick={onClose} data-testid="structure-panel-overlay" />
          <motion.div
            initial={{ x: -420 }} animate={{ x: 0 }} exit={{ x: -420 }}
            transition={{ type: "spring", damping: 30, stiffness: 260 }}
            className="fixed left-0 top-0 bottom-0 z-50 w-[92vw] max-w-[400px] bg-white shadow-2xl flex flex-col"
            data-testid="structure-panel"
          >
            {body}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
